import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, CheckCircle, Wand2 } from "lucide-react";
import { AutomationTemplateSelector } from './AutomationTemplateSelector';
import { AutomationRuleForm } from '../AutomationRuleForm';
import { AutomationTemplate } from '../data/automationTemplates';
import { AutomationRuleFormData } from '../schemas/automationRuleSchema';
import { useAutomationRuleForm } from '../hooks/useAutomationRuleForm';

interface AutomationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (rule: AutomationRuleFormData) => void;
}

type WizardStep = 'template' | 'customize' | 'review';

export const AutomationWizard: React.FC<AutomationWizardProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<AutomationTemplate | null>(null);

  const form = useAutomationRuleForm(
    undefined,
    (data: AutomationRuleFormData) => {
      onComplete(data);
      handleClose();
    }
  );

  const handleClose = () => {
    setCurrentStep('template');
    setSelectedTemplate(null);
    form.form.reset();
    onClose();
  };

  const handleSelectTemplate = (template: AutomationTemplate) => {
    setSelectedTemplate(template);
    form.form.reset(template.ruleData);
    setCurrentStep('customize');
  };

  const handleStartFromScratch = () => {
    setSelectedTemplate(null);
    form.form.reset();
    setCurrentStep('customize');
  };

  const handleNext = () => {
    if (currentStep === 'customize') {
      setCurrentStep('review');
    }
  };

  const handleBack = () => {
    if (currentStep === 'review') {
      setCurrentStep('customize');
    } else if (currentStep === 'customize') {
      setCurrentStep('template');
    }
  };

  const handleCreateRule = () => {
    const formData = form.form.getValues();
    form.handleSubmit(formData);
  };

  const getStepNumber = () => {
    switch (currentStep) {
      case 'template': return 1;
      case 'customize': return 2;
      case 'review': return 3;
      default: return 1;
    }
  };

  const getProgressValue = () => {
    return (getStepNumber() / 3) * 100;
  };

  const canProceed = () => {
    if (currentStep === 'customize') {
      const values = form.form.getValues();
      return values.name && values.description && values.trigger && values.actions?.length > 0;
    }
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Wand2 className="h-6 w-6 text-primary" />
            <div>
              <DialogTitle>Automation Rule Wizard</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Step {getStepNumber()} of 3
              </p>
            </div>
          </div>
          <Progress value={getProgressValue()} className="mt-4" />
        </DialogHeader>

        <div className="space-y-6">
          {currentStep === 'template' && (
            <AutomationTemplateSelector
              onSelectTemplate={handleSelectTemplate}
              onClose={handleStartFromScratch}
            />
          )}

          {currentStep === 'customize' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Customize Your Rule</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedTemplate 
                      ? `Customizing: ${selectedTemplate.name}`
                      : 'Creating a new automation rule from scratch'
                    }
                  </p>
                </div>
              </div>
              
              <AutomationRuleForm
                form={form.form}
                onSubmit={() => {}} // We'll handle submission in review step
                onCancel={handleClose}
                isLoading={false}
                validationResult={form.validationResult}
                isValidating={form.isValidating}
                isWizardMode={true}
              />
            </div>
          )}

          {currentStep === 'review' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">Review & Create</h3>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Rule Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="font-medium">{form.form.getValues('name')}</p>
                    <p className="text-sm text-muted-foreground">
                      {form.form.getValues('description')}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium">Trigger</p>
                      <p className="text-sm text-muted-foreground">
                        {form.form.getValues('trigger.type')?.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Actions</p>
                      <p className="text-sm text-muted-foreground">
                        {form.form.getValues('actions')?.length || 0} action(s) configured
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium">Priority</p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {form.form.getValues('priority')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Status</p>
                      <p className="text-sm text-muted-foreground">
                        {form.form.getValues('enabled') ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <div className="flex justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 'template'}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            
            {currentStep === 'review' ? (
              <Button onClick={handleCreateRule}>
                Create Rule
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
